/**
 * The "Next Action" a PO line shows — who owes the next move.
 *
 * Lifted out of usePOLinesActions (CR-3410 / CR-4696) for Customer review
 * 11 Sep (review item 26b): the PO header's Lines panel showed the line's
 * goods-receipt status ("pending") where the reviewer wants the PO Lines grid's Next
 * Action. One pure function now serves both, so the two cannot drift apart.
 * The party itself comes from the serializer's `waiting_on`, which is derived
 * from acknowledge_status (the old portal's formatNextAction).
 */
import type { PurchaseOrderLine } from '../../lib/api';

export interface NextActionResult {
  text: string;
  className: string;
}

const WAITING_CUSTOMER: NextActionResult = { text: 'Waiting Customer', className: 'bg-blue-100 text-blue-700' };
const WAITING_SUPPLIER: NextActionResult = { text: 'Waiting Supplier', className: 'bg-orange-100 text-orange-700' };
const NO_ACTION: NextActionResult = { text: '-', className: 'text-muted-foreground' };

/**
 * @param line - The PO line.
 * @returns The Next Action label and its badge classes; '-' when nobody owes a move.
 */
export function nextActionFor(line: PurchaseOrderLine): NextActionResult {
  // CR-4696: Closed lines need no further action
  if (line.status === 'received') return NO_ACTION;
  if (line.on_hold) {
    if (line.on_hold_by_company_type === 'customer' || line.on_hold_by_company_type === 'helix') {
      return WAITING_CUSTOMER;
    }
    if (line.on_hold_by_company_type === 'supplier') return WAITING_SUPPLIER;
    return { text: 'On Hold', className: 'bg-gray-100 text-gray-700' };
  }
  if (line.status === 'cancelled') return NO_ACTION;
  if (line.waiting_on === 'Customer') return WAITING_CUSTOMER;
  if (line.waiting_on === 'Supplier') return WAITING_SUPPLIER;
  return NO_ACTION;
}
